import { useApolloClient, useMutation } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { BillingInterval, OtpTransport, SignupSubtype, SignupType } from '@/__generated__/graphql';
import { setUserContext } from '@/actions/ui';
import { SignupSessionError, signup } from '@/auth';
import {
  ACCEPT_PROJECT_INVITATION_MUTATION,
  ACTIVE_PROJECT_MEMBERSHIP_QUERY,
  COMPLETE_SMB_SIGNUP_MUTATION,
  CREATE_SMB_SETUP_INTENT_MUTATION,
  GROUPS_BY_EMAIL_DOMAIN_QUERY,
  PENDING_PROJECT_INVITE_QUERY,
  REQUEST_TO_JOIN_GROUP_MUTATION,
  SEND_PASSWORDLESS_OTP_MUTATION,
  VERIFY_PASSWORDLESS_OTP_MUTATION,
} from '@/auth/store/queries';
import { authSession } from '@/auth/utils/authSession';
import type { UserContext } from '@/core/user';
import { useApp } from '@/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from '@/store';
import { getSameOriginPath, rewriteUrl, safeUrl } from '@/utils';

import type { CompleteSMBSignupInput } from '../components/SubscriptionStep';
import { OTP_EXPIRY_MS } from '../constants';
import type {
  AccountData,
  DomainMatch,
  PendingProjectInvite,
  SelfServiceSMBSignupState,
  SubscriptionResult,
} from '../types';
import { accountStepFieldErrors } from '../utils/accountStepErrors';
import { captureSelfServiceError } from '../utils/sentryScrub';
import { deriveResumeStep, isSignedInWithNothingToResume } from './useResumeStepFromViewer';

// TODO(ENG-5201 follow-up): this hook accumulated five concerns (Apollo
// wiring, callback definitions, resume-step derivation, initialState
// seeding, post-mutation cache reset). Split into
// useSelfServiceSMBSignupCallbacks (Apollo-bound mutations) +
// useSelfServiceSMBSignupBootstrap (resume + initialState + redirect
// guard) once this PR ships. Deferred to keep this PR's blast radius
// scoped to user-visible review-board fixes.

interface UseSelfServiceSMBSignupModalOptions {
  // Where to navigate when the user clicks "Go to dashboard" on the
  // confirmation step, or when the page-level "already signed up" guard
  // fires. Defaults to '/dashboard'. Pass the current page URL on inline
  // mounts so the user lands back where they were trying to go.
  nextUrl?: string;
  // Skip the "already signed up → redirect" guard. Set this on inline
  // mounts that want to render their own page content underneath the modal
  // and don't care about routing the user away.
  skipHasTeamBillingRedirect?: boolean;
  // Parent-owned modal close handler. When the viewer is already on
  // /dashboard, the terminal-step CTA dismisses the modal instead of
  // navigating to a page they're already on.
  onClose?: () => void;
}

interface SelfServiceSMBSignupModalCallbacks {
  initialState?: Partial<SelfServiceSMBSignupState>;
  trialDays?: number;
  onSignup: (data: AccountData) => Promise<void>;
  onSendEmailOtp: (email: string) => Promise<void>;
  onVerifyEmailOtp: (email: string, code: string) => Promise<{ token: string; expiresAt: number }>;
  onFetchDomainMatches: () => Promise<DomainMatch[]>;
  onFetchPendingInvite: (otpVerificationToken?: string) => Promise<PendingProjectInvite | null>;
  onFetchActiveProjectMembership: (otpVerificationToken?: string) => Promise<boolean>;
  onAcceptInvite: (token: string) => Promise<void>;
  onRequestToJoin: (input: {
    groupId: string;
    otpVerificationToken?: string;
  }) => Promise<{ autoApproved: boolean }>;
  onCreateSetupIntent: (input: {
    otpVerificationToken?: string;
    companyName?: string;
  }) => Promise<{ clientSecret: string }>;
  onCompleteSMBSignup: (input: CompleteSMBSignupInput) => Promise<SubscriptionResult>;
  onGoToDashboard: () => void;
  onGoToProjects: () => void;
  ctaLabel?: string;
}

interface UseSelfServiceSMBSignupModalResult {
  // Spread onto <SelfServiceSMBSignupModal {...modalProps} open={...} onClose={...} />.
  // Excludes `open` and `onClose` (each caller manages its own open state
  // and close behavior — e.g. Dashboard just toggles state; the dedicated
  // /signup/customer page navigates away).
  modalProps: SelfServiceSMBSignupModalCallbacks;
  // True when the viewer already has team billing on first mount. Callers
  // can use this to bail out (e.g. don't render the modal at all) on the
  // dedicated /signup/customer page; on inline mounts where there's also
  // useful page content behind the modal, you can ignore this and just
  // skip opening the modal.
  initialHasTeamBilling: boolean;
  // True when deriveResumeStep returned a non-null step — i.e. the viewer
  // is an SMB-no-billing user with mid-flow state worth resuming. Callers
  // that auto-open the modal (e.g. /signup/customer, dashboard banner CTA)
  // can use this; callers that open on user action (e.g. CTA click) can
  // ignore it.
  isResumable: boolean;
  // True when the viewer was signed in on mount with nothing to resume —
  // fully signed up, or outside the SMB self-service cohort (ENG-5769).
  // The modal has no step for them: it would open at `account` and offer an
  // account they already have. Callers that auto-open must not render it.
  // Read at mount, so mount only once the viewer has loaded (route actions
  // guarantee this).
  skipModal: boolean;
}

// Hook that builds every prop the SelfServiceSMBSignupModal needs from the
// current viewer + URL state. Owns the Apollo wiring, the 6 mutation
// callbacks, resume-step derivation, initialState seeding, and trialDays
// parsing — everything that used to live in CustomerRegisterPage.tsx so
// that any caller (dashboard, profile, expert-request) can mount the modal
// inline over their own page content rather than redirecting users into a
// dedicated signup route with a synthetic blurred background.
export function useSelfServiceSMBSignupModal(
  options: UseSelfServiceSMBSignupModalOptions = {}
): UseSelfServiceSMBSignupModalResult {
  const { nextUrl, skipHasTeamBillingRedirect, onClose } = options;
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const { graphql } = useApp();
  // When the viewer is already on /dashboard and no nextUrl is set, the
  // terminal-step CTA would otherwise navigate to a page they're on and the
  // modal would stay open. Dismiss instead.
  const dismissInsteadOfDashboard = !nextUrl && location.pathname === '/dashboard';

  const dispatch = useAppDispatch();
  const viewer = useAppSelector((state) => state.viewer);
  const resumeDecision = useMemo(
    () =>
      deriveResumeStep({
        id: viewer.id || null,
        signup_type: viewer.signup_type || null,
        email: viewer.email ? { verified: viewer.email.verified ?? false } : null,
        groups: viewer.groups,
        pendingTeamMembership: viewer.pendingTeamMembership ?? null,
      }),
    [viewer.id, viewer.signup_type, viewer.email, viewer.groups, viewer.pendingTeamMembership]
  );

  // R-5: if the viewer was already fully signed up on landing (billing
  // exists), bounce to the dashboard. Snapshot at mount so billing created
  // mid-flow (which refreshes the viewer mid-confirmation) doesn't trigger
  // this redirect — the confirmation step's CTA handles that navigation.
  const initialHasTeamBillingRef = useRef(resumeDecision.hasTeamBilling);
  // Snapshot at mount for the same reason: completing signup mid-flow nulls
  // the live step, and a live read would unmount the modal before its
  // confirmation step renders.
  const skipModalRef = useRef(isSignedInWithNothingToResume(resumeDecision));
  // Stripe customer ID is captured locally (not via Sentry.setTag) and
  // attached only to error events emitted from this flow. Avoids leaking
  // the ID into unrelated session errors.
  const stripeCustomerIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (skipHasTeamBillingRedirect) return;
    if (initialHasTeamBillingRef.current) {
      navigate(nextUrl || '/dashboard', { replace: true });
    }
  }, [navigate, nextUrl, skipHasTeamBillingRedirect]);

  // Seed initialState. For anonymous users the modal opens at `account`
  // (the default). For mid-flow users we seed the resume step plus form
  // data we already know (email, name). A signed-in viewer with nothing to
  // resume also gets no step here; skipModal is what keeps them out.
  const initialState = useMemo<Partial<SelfServiceSMBSignupState> | undefined>(() => {
    if (!resumeDecision.step) return undefined;
    const base: Partial<SelfServiceSMBSignupState> = {
      step: resumeDecision.step,
      account: {
        email: viewer.email?.address ?? '',
        firstName: viewer.first_name ?? '',
        lastName: viewer.last_name ?? '',
      },
    };
    // Seed the team-request-sent step with the pending team's name so the
    // status disclaimer renders identical copy whether we just landed on it
    // (selectedMatch set in-session by chooseDomainMatch) or resumed from a
    // fresh page load (selectedMatch is empty).
    const pending = viewer.pendingTeamMembership;
    if (resumeDecision.step === 'team-request-sent' && pending?.teamId && pending?.teamName) {
      base.selectedMatch = { id: pending.teamId, name: pending.teamName };
    }
    return base;
  }, [
    resumeDecision.step,
    viewer.email,
    viewer.first_name,
    viewer.last_name,
    viewer.pendingTeamMembership,
  ]);

  // R-4: trial_days is read once from the URL at hook mount. The URL is
  // the source of truth — out-of-set values like ?trial_days=30 are
  // rejected server-side with billing.invalid_trial_days and surface a
  // "Continue without trial" CTA (see SubscriptionStep#continueWithoutTrial).
  // Cross-session resume is naturally safe: a returning user lands on a
  // URL without the param, so trialDays is undefined. Prior versions gated
  // this read on resumeDecision.step, but that step is viewer-derived and
  // flips off the moment signup() authenticates the new token mid-flow —
  // which silently stripped the URL value before the API ever saw it.
  const trialDays = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('trial_days');
    if (!raw) return undefined;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, []);

  const [sendOtp] = useMutation(SEND_PASSWORDLESS_OTP_MUTATION);
  const [verifyOtp] = useMutation(VERIFY_PASSWORDLESS_OTP_MUTATION);
  const [requestToJoinGroup] = useMutation(REQUEST_TO_JOIN_GROUP_MUTATION);

  // Step A: createUser with signup_subtype: "self_service" so the API
  // suppresses the legacy welcome+verify-link emails (email-OTP replaces
  // them). signup(..., false) skips the redirect and returns the auth
  // token; we then authenticate it so subsequent Apollo requests
  // (groupsByEmailDomain, requestToJoinGroup, completeSMBSignup) carry the
  // Bearer header.
  const onSignup = useCallback(
    async (data: AccountData) => {
      try {
        const token = await signup(
          graphql,
          {
            email: data.email,
            password: data.password,
            first_name: data.firstName,
            last_name: data.lastName,
            signup_type: SignupType.Smb,
            signup_subtype: SignupSubtype.SelfService,
          },
          false
        );
        if (!token) {
          throw new Error('Signup did not return an auth token');
        }
        await authSession.authenticate(token);
      } catch (err) {
        // signup() reports a SignupSessionError itself (ENG-5760). The field
        // errors AccountStep shows inline are expected outcomes, but only for
        // an anonymous visitor. A session reaching createUser is the ENG-5769
        // bug, so it still reports. Read the session now rather than at
        // mount: "Edit email" returns a viewer who signed in mid-flow, or
        // resumed at email-otp, to this step.
        const expected = !!accountStepFieldErrors(err) && !authSession.getToken();
        if (!(err instanceof SignupSessionError) && !expected) {
          captureSelfServiceError(err, { step: 'account' });
        }
        throw err;
      }
    },
    [graphql]
  );

  // Step A.5: email-OTP send. Same sendPasswordlessOTP mutation as the SMS
  // path, with transport: EMAIL. The API generates a 6-digit code, bcrypts +
  // stores it, and sends via SendGrid (or logs in DEBUG mode).
  const onSendEmailOtp = useCallback(
    async (email: string) => {
      try {
        const { data: result } = await sendOtp({
          variables: { address: email, transport: OtpTransport.Email },
        });
        if (!result?.sendPasswordlessOTP?.success) {
          throw new Error('Failed to send verification code');
        }
      } catch (err) {
        captureSelfServiceError(err, { step: 'email-otp' });
        throw err;
      }
    },
    [sendOtp]
  );

  // Step A.5: email-OTP verify. Returns a token whose claim carries the
  // verified email (not phone); passed to completeSMBSignup as
  // otpVerificationToken on the self-service path.
  const onVerifyEmailOtp = useCallback(
    async (email: string, code: string) => {
      try {
        const { data: verifyResult } = await verifyOtp({
          variables: { address: email, code, transport: OtpTransport.Email },
        });
        const otpToken = verifyResult?.verifyPasswordlessOTP?.otpVerificationToken;
        if (!otpToken) throw new Error('Verification failed');
        // Refresh viewer so Redux sees email.verified=true. Without this,
        // abandoning post-verify and landing on /dashboard would still
        // derive resumeDecision.step='email-otp' from the stale viewer.
        const authToken = authSession.getToken();
        if (authToken) {
          try {
            await authSession.authenticate(authToken);
          } catch (refreshErr) {
            captureSelfServiceError(refreshErr, {
              step: 'email-otp',
              substep: 'post_verify_viewer_refresh',
            });
          }
        }
        // TODO(ENG-5201 follow-up): once the verifyPasswordlessOTP mutation
        // exposes a server-authoritative `expiresAt`, swap to that. Today
        // the client computes Date.now() + OTP_EXPIRY_MS to match the
        // server's 15-min TTL; clock skew on the user's device can fire
        // the client-side expiry timer early or late, but the server is
        // the actual gate via AUTH_TOKEN_EXPIRED.
        return { token: otpToken, expiresAt: Date.now() + OTP_EXPIRY_MS };
      } catch (err) {
        captureSelfServiceError(err, { step: 'email-otp' });
        throw err;
      }
    },
    [verifyOtp]
  );

  // Domain-match lookup runs with Bearer auth post-signup. The page-level
  // Apollo client captured auth state in its setContext closure at
  // construction time, so we build a fresh authenticated client from the
  // cached token to guarantee the Authorization header reaches the server.
  const onFetchDomainMatches = useCallback(async () => {
    try {
      const client = authSession.createClient();
      const result = await client.query(GROUPS_BY_EMAIL_DOMAIN_QUERY);
      const groups = result?.groupsByEmailDomain ?? [];
      return groups.map((g: { id: string; name?: string | null }) => ({
        id: g.id,
        name: g.name ?? '',
      }));
    } catch (err) {
      captureSelfServiceError(err, { step: 'email-otp' });
      throw err;
    }
  }, []);

  // Pending-project-invite lookup. Mirrors onFetchDomainMatches above: build
  // a fresh authenticated client so Bearer auth reaches the server. Accepts
  // an optional OTP token so the same callback can serve both the
  // email-otp-verify call site and the dashboard-resume call site -- both
  // always hold a Bearer token by the time they call this (AccountStep's
  // signup() already ran authSession.authenticate, and the resume path is
  // authenticated by definition), so the OTP token here is belt-and-braces,
  // not the primary credential.
  //
  // Doesn't capture failures itself (review fix #16): four call sites
  // consume this (EmailOtpStep, this hook's own resume effect,
  // SubscriptionStep's early check, usePendingInviteGuard), and every one
  // already reports the same rejection under its own accurate tag -- a
  // capture here would just double-report under a second, less specific
  // one. Same shape as onFetchActiveProjectMembership below.
  const onFetchPendingInvite = useCallback(async (otpVerificationToken?: string) => {
    const client = authSession.createClient();
    const result = await client.query(
      PENDING_PROJECT_INVITE_QUERY,
      otpVerificationToken ? { otpVerificationToken } : undefined
    );
    return result?.pendingProjectInvite ?? null;
  }, []);

  // Active-project-membership lookup (ENG-5334 follow-up). Same dual-auth
  // query shape and no-capture rationale as onFetchPendingInvite above --
  // reports failures at the call site (SubscriptionStep's early-check
  // effect) instead of here.
  const onFetchActiveProjectMembership = useCallback(async (otpVerificationToken?: string) => {
    const client = authSession.createClient();
    const result = await client.query(
      ACTIVE_PROJECT_MEMBERSHIP_QUERY,
      otpVerificationToken ? { otpVerificationToken } : undefined
    );
    return result?.activeProjectMembership ?? false;
  }, []);

  // Accepts the pending project invite surfaced by resolveEmailOtpVerification.
  // Mirrors the sibling callbacks above: build a fresh authenticated client
  // via authSession.createClient() rather than dispatch(acceptInvitation(token))
  // (actions/invitation.ts) — that thunk rides the app-boot Apollo client's
  // stale/absent bearer token, the exact bug this file already works around
  // for onFetchDomainMatches/onCreateSetupIntent/onCompleteSMBSignup.
  //
  // On success, mirrors RegisterPage.tsx's post-accept redirect handling:
  // navigate to the server-returned URL (rewritten via rewriteUrl for
  // cross-domain rewrites) when present, otherwise fall back to the same
  // nextUrl/dashboard-dismiss logic as onGoToDashboard. Joining a project is
  // a terminal action for this flow — there's no further step to render, so
  // the callback itself navigates away and dismisses the modal.
  const onAcceptInvite = useCallback(
    async (token: string) => {
      let redirectTo: string | null | undefined;
      try {
        const client = authSession.createClient();
        const data = await client.mutate(ACCEPT_PROJECT_INVITATION_MUTATION, { token });
        redirectTo = data?.acceptInvitation;
      } catch (err) {
        captureSelfServiceError(err, { step: 'invite-accept' });
        throw err;
      }
      // acceptInvitation returns only a URL, not a fresh token, so nothing
      // else refreshes the viewer (review fix #11). Without this, Redux
      // keeps the pre-join snapshot (signup_type: smb, no team/billing) --
      // exactly what deriveResumeStep reads -- so the user could still be
      // classed resumable after joining for free. Best-effort, same
      // try/captureSelfServiceError shape as the neighbours: doesn't block
      // navigation now that the accept itself already succeeded.
      try {
        const token = authSession.getToken();
        if (token) await authSession.authenticate(token);
      } catch (err) {
        captureSelfServiceError(err, {
          step: 'invite-accept',
          substep: 'post_accept_viewer_refresh',
        });
      }
      // The user's project membership changed; reset the page-level Apollo
      // cache so the destination page doesn't render stale (pre-membership)
      // data. Best-effort: a cache-reset failure shouldn't block navigation
      // now that the accept itself already succeeded — mirrors the
      // non-fatal viewer-refresh handling in onCompleteSMBSignup/
      // onRequestToJoin below (those swallow refreshErr rather than
      // failing a mutation that already succeeded).
      try {
        await apolloClient.resetStore();
      } catch (err) {
        captureSelfServiceError(err, {
          step: 'invite-accept',
          substep: 'post_accept_cache_reset',
        });
      }
      if (redirectTo) {
        // acceptInvitation returns a full absolute URL (mirrors
        // RegisterPage.tsx's loader, which hands it to react-router's
        // redirect() and navigates correctly regardless). navigate() here
        // is the imperative SPA router, it has no concept of an absolute
        // URL and would otherwise append it onto the current route,
        // producing a malformed nested path. Extract the same-origin path
        // first; only hard-navigate if it's genuinely cross-origin.
        const rewritten = rewriteUrl(redirectTo);
        const samePath = getSameOriginPath(rewritten);
        if (samePath) {
          navigate(samePath);
        } else if (rewritten.startsWith('/')) {
          navigate(rewritten);
        } else if (safeUrl(rewritten)) {
          // Defense-in-depth (review fix #18): rewriteUrl only strips a
          // configured prefix and can't neutralise a dangerous scheme.
          // safeUrl allow-lists http/https before this ever reaches
          // window.location.href.
          window.location.href = rewritten;
        } else {
          navigate(nextUrl || '/dashboard');
        }
        onClose?.();
      } else if (dismissInsteadOfDashboard && onClose) {
        onClose();
      } else {
        navigate(nextUrl || '/dashboard');
        onClose?.();
      }
    },
    [apolloClient, navigate, nextUrl, dismissInsteadOfDashboard, onClose]
  );

  const onRequestToJoin = useCallback(
    async ({
      groupId,
      otpVerificationToken,
    }: {
      groupId: string;
      otpVerificationToken?: string;
    }): Promise<{ autoApproved: boolean }> => {
      try {
        // Omit the variable when no OTP token is in-memory (resume path)
        // so the server falls back to Bearer auth from the session.
        const { data } = await requestToJoinGroup({
          variables: { groupId, otpVerificationToken: otpVerificationToken ?? null },
        });
        // requestToJoinGroup issues a fresh Bearer token so the OTP-only
        // signup has a normal session afterward. Install it via
        // authSession.authenticate so the viewer/Redux store reflects the
        // new identity, then reset Apollo cache so the post-join team
        // (when auto-approved via invitation) shows up without a manual
        // reload — same fix as the completeSMBSignup path.
        const token = data?.requestToJoinGroup?.token;
        // member.state === 'active' means the API auto-approved via an
        // existing email-only invitation — no owner approval needed.
        const memberState = data?.requestToJoinGroup?.member?.state;
        const autoApproved = memberState === 'active';
        if (token) {
          try {
            await authSession.authenticate(token);
            // Reset BEFORE switching the context (WEBAPP-CE6): the switch
            // makes the dashboard start its consultation queries, and a
            // resetStore after it cancels them in flight (Apollo invariant
            // #42, an unhandled rejection that also leaves the lists empty).
            // try/finally keeps ENG-5251's guarantee that both steps run:
            // resetStore rejects when any active query's refetch fails, and
            // that must not strand the user in the old context.
            try {
              await apolloClient.resetStore();
            } finally {
              // ENG-5251: when the join is auto-approved (invitation match),
              // the user is now a member of `groupId` — route them into that
              // team's dashboard context for the same reason as the
              // completeSMBSignup path. Pending-approval joiners have no team
              // yet, so leaving them on the default 'client' context is
              // correct.
              if (autoApproved) {
                await dispatch(setUserContext(groupId as UserContext));
              }
            }
          } catch (refreshErr) {
            captureSelfServiceError(refreshErr, {
              step: 'team-join',
              substep: 'post_request_viewer_refresh',
            });
          }
        }
        return { autoApproved };
      } catch (err) {
        captureSelfServiceError(err, { step: 'team-join' });
        throw err;
      }
    },
    [requestToJoinGroup, apolloClient, dispatch]
  );

  // Mirrors the partner-side pattern in NewCustomerSignupModal/PaymentStep:
  // the page-level Apollo client's authLink captures auth state in its
  // closure at construction time, so it has no Authorization header on
  // resume. Build a fresh authenticated client from the cached token so
  // Bearer auth reaches the server when the OTP token isn't supplied.
  const onCreateSetupIntent = useCallback(
    async ({
      otpVerificationToken,
      companyName,
    }: {
      otpVerificationToken?: string;
      companyName?: string;
    }) => {
      try {
        const client = authSession.createClient();
        const data = await client.mutate(CREATE_SMB_SETUP_INTENT_MUTATION, {
          input: {
            ...(otpVerificationToken ? { otpVerificationToken } : {}),
            ...(companyName ? { companyName } : {}),
          },
        });
        const clientSecret = data?.createSMBSetupIntent?.clientSecret;
        if (!clientSecret) throw new Error('Could not initialize Stripe payment form');
        const stripeCustomerId = data?.createSMBSetupIntent?.stripeCustomerId;
        // Remember the customer ID so subsequent error captures in this
        // signup flow can scope to it. We deliberately do NOT call
        // Sentry.setTag (which mutates global scope and would leak the ID
        // into unrelated errors for the rest of the session).
        if (stripeCustomerId) stripeCustomerIdRef.current = stripeCustomerId;
        return { clientSecret };
      } catch (err) {
        captureSelfServiceError(err, {
          step: 'subscription',
          ...(stripeCustomerIdRef.current
            ? { stripe_customer_id: stripeCustomerIdRef.current }
            : {}),
        });
        throw err;
      }
    },
    []
  );

  // Self-service mirrors the partner-driven SMB pattern: omit
  // paymentMethodId and let the API auto-discover via paymentmethod.List.
  // The resolver derives isSelfService server-side from (otpToken != nil ||
  // signup_status == pending_payment), so no client flag is needed — on
  // resume the user's signup_status is still pending_payment until commit.
  const onCompleteSMBSignup = useCallback(
    async (input: CompleteSMBSignupInput) => {
      try {
        const client = authSession.createClient();
        const data = await client.mutate(COMPLETE_SMB_SIGNUP_MUTATION, {
          input: {
            ...(input.otpVerificationToken
              ? { otpVerificationToken: input.otpVerificationToken }
              : {}),
            billingInterval:
              input.billingInterval === 'YEAR' ? BillingInterval.Year : BillingInterval.Month,
            seatCount: input.seatCount,
            idempotencyKey: input.idempotencyKey,
            ...(input.trialDays !== undefined ? { trialDays: input.trialDays } : {}),
            ...(input.overridePendingInviteGuard ? { overridePendingInviteGuard: true } : {}),
          },
        });
        const result = data?.completeSMBSignup;
        if (!result) throw new Error('Subscription completion returned no result');
        // Refresh viewer so Redux sees the new billing_account on its
        // group, then reset the page-level Apollo cache so subsequent
        // queries see the freshly-provisioned team/billing rows. Without
        // resetStore, the page cache holds the pre-signup empty results
        // (cache-first default), so Settings shows no team until reload.
        //
        // Order matters: authenticate() runs BEFORE resetStore(). The
        // alternative (resetStore first) leaves a window where the cache
        // is empty and watched queries refetch without an auth token,
        // 401ing visibly. The current order keeps the auth context fresh
        // and bounded the stale-cache window to a single React render
        // commit, which is the lesser harm.
        if (result.token) {
          try {
            await authSession.authenticate(result.token);
            // Reset before switching the context, both always run: same
            // shape as onRequestToJoin above, see the comment there
            // (WEBAPP-CE6, ENG-5251). The authenticate → resetStore order
            // documented just above is preserved.
            const newTeamId = result.team?.id;
            try {
              await apolloClient.resetStore();
            } finally {
              // ENG-5251: route the brand-new owner into their team's
              // dashboard context. Without this, the first
              // authSession.authenticate() (after the account step, before any
              // team exists) locked the context to 'client', and
              // getDefaultUserContext's "preserve currentContext" branch
              // (core/user.ts) keeps it 'client' even after the team is
              // provisioned — landing the user on the personal dashboard
              // instead of the team dashboard.
              if (newTeamId) {
                await dispatch(setUserContext(newTeamId as UserContext));
              }
            }
          } catch (refreshErr) {
            captureSelfServiceError(refreshErr, {
              step: 'subscription',
              substep: 'post_complete_viewer_refresh',
            });
          }
        }
        // The completeSMBSignup mutation does not yet expose trialEnd (the
        // API ENG-5201 PR introduces it on Contract). We synthesize a
        // trialEnd timestamp client-side from the trialDays we sent so the
        // confirmation copy can branch trial-vs-paid correctly. Once API
        // ships the field we should swap to the server's value.
        const trialDaysGranted = input.trialDays ?? null;
        const trialEnd =
          trialDaysGranted && trialDaysGranted > 0
            ? new Date(Date.now() + trialDaysGranted * 24 * 60 * 60 * 1000).toISOString()
            : null;
        return {
          trialEnd,
          trialDaysGranted,
          status: 'active',
          contractId: result.billingAccount?.id ?? '',
        };
      } catch (err) {
        captureSelfServiceError(err, {
          step: 'subscription',
          idempotency_key: input.idempotencyKey,
          billing_interval: input.billingInterval,
          ...(input.trialDays !== undefined ? { trial_days_granted: String(input.trialDays) } : {}),
          ...(stripeCustomerIdRef.current
            ? { stripe_customer_id: stripeCustomerIdRef.current }
            : {}),
        });
        throw err;
      }
    },
    [apolloClient, dispatch]
  );

  const onGoToDashboard = useCallback(() => {
    if (dismissInsteadOfDashboard && onClose) {
      onClose();
      return;
    }
    navigate(nextUrl || '/dashboard');
    // The modal lives in a persistent layout (e.g. AccessHeader), so
    // navigation alone doesn't unmount it. Close the open state too so a
    // one-click "Go to dashboard" actually leaves the user on the dashboard
    // without a stale modal to dismiss.
    onClose?.();
  }, [dismissInsteadOfDashboard, onClose, navigate, nextUrl]);

  // ENG-5334 follow-up: distinct from onGoToDashboard above — the
  // active-membership guard's "you already belong to a project" dialogs
  // (SubscriptionStep) send the user to their expert requests, not the
  // dashboard, regardless of where the modal was mounted (no
  // dismissInsteadOfDashboard special-case here).
  const onGoToProjects = useCallback(() => {
    navigate('/expert_requests');
    onClose?.();
  }, [navigate, onClose]);

  return {
    modalProps: {
      initialState,
      trialDays,
      onSignup,
      onSendEmailOtp,
      onVerifyEmailOtp,
      onFetchDomainMatches,
      onFetchPendingInvite,
      onFetchActiveProjectMembership,
      onAcceptInvite,
      onRequestToJoin,
      onCreateSetupIntent,
      onCompleteSMBSignup,
      onGoToDashboard,
      onGoToProjects,
      // On inline mounts (nextUrl pointing back to a profile/expert page),
      // the CTA returns the user to that page — not the dashboard. When
      // there's no nextUrl and the viewer is already on /dashboard, the
      // CTA dismisses the modal. Otherwise it's "Go to your dashboard".
      ctaLabel: nextUrl
        ? 'Continue'
        : dismissInsteadOfDashboard
          ? 'Dismiss'
          : 'Go to your dashboard',
    },
    initialHasTeamBilling: initialHasTeamBillingRef.current,
    isResumable: resumeDecision.step !== null,
    skipModal: skipModalRef.current,
  };
}
